/* eslint-disable @typescript-eslint/no-var-requires */
import React, { useState, useEffect, useRef } from 'react';
import { isEqual } from 'lodash';

import GraphsContainer from '../../../components/GraphsContainer';
import Graph from '../components/Graph';

import { CopyData, GraphData, GraphDataObject, ModalFilter } from '../../../types';

import { formatDateFilter } from '../../../helpers/format';
import { returnParams, fetchEndpoint } from '../helpers';
import { endpoints, handleFetchEndpoint } from '../configs';

import { useSelector } from 'react-redux';
import { ReduxStates } from '../../../redux/reduxTypes';
import { demoSelectedGraph } from '../../../helpers/demoValues';

interface Props {
  marketFilters: (ModalFilter | null)[];
  chosenSegmentIndex: number;
  selectedCurrency?: string | null;
  dateFilter: string;
  customRangeFilter?: (number | string)[] | null;
  currencyCode?: string | null;
  setStatus: (arg1: any) => void;
  copyData: CopyData;
  showPlanPercentForNetSales?: boolean;
  showWidgets?: boolean;
}

const SectionDetailGraph = ({
  marketFilters,
  chosenSegmentIndex,
  selectedCurrency,
  dateFilter,
  customRangeFilter,
  currencyCode,
  copyData,
  showPlanPercentForNetSales,
  setStatus,
  showWidgets
}: Props): React.ReactElement => {
  const parentRef: any = useRef(null);
  const combinedGraphEl: any = useRef(null);
  const client = useSelector((state: ReduxStates) => state.client);
  const data = useSelector((state: ReduxStates) => state.data);
  const [showDownloadOptions, setShowDownloadOptions] = useState<boolean>(false);

  const [selectedGraph, setSelectedGraph] = useState<GraphDataObject | null>(null);
  const [selectedGraphsData, setSelectedGraphsData] = useState<Array<GraphData> | null>(null);
  const [graphsDataUnavailable, setGraphsDataUnavailable] = useState<boolean>(false);
  const [fetchingGraphs, setFetchingGraphs] = useState<string[]>([]);
  const [graphsData, setGraphsData] = useState<Array<any> | null>(null);

  const fetchGraphsData = (path: string | number, params: unknown) => {
    const target = endpoints[path]?.graph?.filter((e) => e.index === chosenSegmentIndex);

    if (!target || !target.length) {
      !graphsData && setGraphsData([]);
      return;
    }

    target?.forEach(({ endpoint, id, title }) => {
      // if (fetchingGraphs.includes(endpoint)) return;
      (async () => {
        try {
          if (!(!showWidgets && id === 'mediaSPGR')) {
            setFetchingGraphs((fetchingGraphs: string[]) => [...fetchingGraphs, endpoint]);
            const data = await fetchEndpoint(endpoint, params, title);
            setGraphsData(() => [...handleFetchEndpoint[path].graphs(id, data)]);
            setFetchingGraphs(fetchingGraphs.filter((e) => e !== endpoint));
          }
        } catch (error) {
          setFetchingGraphs(fetchingGraphs.filter((e) => e !== endpoint));
          if (!graphsData || (graphsData && graphsData.length)) setGraphsData([]);
          console.error(error);
          setStatus({ state: 'error', message: 'Something went wrong fetching graph data. Please try again.' });
        }
      })();
    });
  };

  const handleGraphUpdate = () => {
    let selectedGraph;

    if (graphsData) {
      const graph: GraphDataObject[] = graphsData.filter(
        (graph: GraphDataObject) => graph.index === chosenSegmentIndex
      );
      selectedGraph = graph;
    } else {
      selectedGraph = null;
    }

    if (graphsData?.length && selectedGraph?.length) {
      if (selectedGraph[0].data) {
        if (client === 'demo' && selectedGraph[0].title === 'Spend As % of Gross <br/> Revenue') {
          switch (dateFilter) {
            case 'This Month':
              selectedGraph = demoSelectedGraph[0];
              break;
            case 'Last Month':
              selectedGraph = demoSelectedGraph[1];
              break;
            case 'Last 3 Months':
              selectedGraph = demoSelectedGraph[2];
              break;
            case 'Last 6 Months':
              selectedGraph = demoSelectedGraph[3];
              break;
            case 'Last 12 Months':
              selectedGraph = demoSelectedGraph[4];
              break;
            case 'Year to Date':
              selectedGraph = demoSelectedGraph[5];
              break;
            default:
              selectedGraph = demoSelectedGraph[5];
          }
        }
        isEqual(selectedGraph[0].data, selectedGraphsData) ? null : setSelectedGraph(selectedGraph[0]);
        isEqual(selectedGraph[0].data, selectedGraphsData)
          ? null
          : setSelectedGraphsData(selectedGraph.flatMap((item: any) => item.data));
      } else {
        setSelectedGraph(selectedGraph[0]);
        setGraphsDataUnavailable(true);
      }
    } else if (graphsData && !graphsData.length) {
      setSelectedGraphsData([]);
    }
  };

  const resetGraphData = () => {
    setGraphsDataUnavailable(false);
    setGraphsData(null);
    setSelectedGraphsData(null);
  };

  // fetch data
  useEffect(() => {
    //reset graphs data
    resetGraphData();

    if (selectedCurrency && marketFilters.length && dateFilter && data.dateFilterRangeLimit) {
      const { params, path } = returnParams(
        marketFilters,
        formatDateFilter(dateFilter, customRangeFilter || undefined, copyData, data.dateFilterRangeLimit),
        data.dateFilterRangeLimit
      );
      fetchGraphsData(path, params);
    }
  }, [selectedCurrency, marketFilters, chosenSegmentIndex, dateFilter, customRangeFilter, data.dateFilterRangeLimit]);

  // data updated
  useEffect(() => {
    handleGraphUpdate();
  }, [graphsData]);

  return (
    <GraphsContainer
      selectedSegment={chosenSegmentIndex === -1 ? '' : selectedGraph?.title || ''}
      parentRef={parentRef}
      data={selectedGraphsData}
      marketFilters={marketFilters}
      showExportOptions={showDownloadOptions}
      setShowExportOptions={setShowDownloadOptions}
    >
      <Graph
        currencyCode={currencyCode}
        selectedCurrency={(selectedGraphsData && 
                           selectedGraphsData[0] &&
                           selectedGraphsData[0].currentValueType === 'currency' && selectedCurrency)
                            || ''}
        graphData={selectedGraphsData}
        showDownloadOptions={showDownloadOptions}
        setShowDownloadOptions={setShowDownloadOptions}
        combinedGraphEl={combinedGraphEl}
        unavailableData={graphsDataUnavailable}
        showPlanData={selectedGraph?.title === 'Net Sales' ? showPlanPercentForNetSales : selectedGraph?.showPlanData}
      />
    </GraphsContainer>
  );
};

export default SectionDetailGraph;
